import { ChangeDetectorRef, Component, ElementRef, HostListener, Input, OnDestroy, ViewChild } from '@angular/core';
import * as feather from 'feather-icons';
import { AuthService } from '../../../core/services/auth.service';
import { Router } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { OrganisationService } from '../../../core/services/organisation.service';
import { AssetsService } from '../../../core/services/assets.service';
import { AppNotification, NotificationsService } from '../../../core/services/notifications.service';
import { Subscription, firstValueFrom } from 'rxjs';
import { PersonalSettingsService } from '../../../core/services/personal-settings';
import { ChatWidgetStateService } from '../../../core/services/chat-widget-state.service';
import { EmailClientRealtimeService } from '../../../core/services/email-client-realtime';
import { EmailMessageSummary } from '../../../core/services/email-client.types';
import { HeaderSearchResult, HeaderService } from './header.service';
import { EmploymentServices } from '../../../core/services/employment.services';
import { BusinessDocument } from '../../../core/services/business-document.service';

@Component({
  selector: 'app-header',
  standalone: false,
  templateUrl: './header.html',
  styleUrl: './header.scss',
})
export class Header implements OnDestroy {

  @Input() loadOrganisationContext = true;
  @ViewChild('globalSearchInput') globalSearchInput?: ElementRef<HTMLInputElement>;

  user: any;
  userAvatarUrl: string | null = null;
  currentOrganisation: any;
  contextLoading = false;
  contextError = '';
  private destroyed = false;

  organisations: any = [];
  services: any = []
  servicesDropdownOpen = false;
  theme: 'light' | 'dark' = document.documentElement.getAttribute('data-bs-theme') === 'dark' ? 'dark' : 'light';
  organisationDropdownOpen = false;
  notificationsDropdownOpen = false;
  emailDropdownOpen = false;
  profileDropdownOpen = false;
  activeEmploymentCount: number | null = null;
  pendingVerificationCount: number | null = null;
  private badgeRequest = 0;
  searchOpen = false;
  searchLoading = false;
  searchQuery = '';
  searchResults: HeaderSearchResult[] = [];
  private searchTimer?: ReturnType<typeof setTimeout>;
  private searchRequest = 0;
  notifications: AppNotification[] = [];
  unreadNotifications = 0;
  unseenNotifications = 0;
  emailUnreadCount = 0;
  recentEmails: EmailMessageSummary[] = [];
  private notificationsSubscription?: Subscription;
  private unreadNotificationsSubscription?: Subscription;
  private unseenNotificationsSubscription?: Subscription;
  private personalSettingsSubscription?: Subscription;
  private avatarSubscription?: Subscription;
  private emailUnreadSubscription?: Subscription;
  private recentEmailsSubscription?: Subscription;
  private organisationSwitchedSubscription?: Subscription;
  private appVisibilitySubscription?: Subscription;

  constructor(
    private authService: AuthService,
    private router: Router,
    private sanitizer: DomSanitizer,
    private companyService: OrganisationService,
    private assetsService: AssetsService,
    private cdr: ChangeDetectorRef,
    private readonly notificationsService: NotificationsService,
    private readonly personalSettings: PersonalSettingsService,
    public readonly chatState: ChatWidgetStateService,
    private readonly emailRealtime: EmailClientRealtimeService,
    private readonly headerService: HeaderService,
    private readonly employments: EmploymentServices,
    private readonly documents: BusinessDocument,
  ) {

  }

  ngOnInit() {
    this.organisationSwitchedSubscription = this.authService.organisationSwitched$.subscribe(() => {
      void this.refreshProfileBadges();
      if (this.loadOrganisationContext) {
        void this.refreshContext();
      }
    });
    this.personalSettingsSubscription = this.personalSettings.current$.subscribe((user) => {
      if (user) {
        this.user = user;
        this.cdr.detectChanges();
      }
    });
    this.avatarSubscription = this.personalSettings.avatarUrl$.subscribe((url) => {
      this.userAvatarUrl = url;
      this.cdr.detectChanges();
    });
    void this.refreshContext();
    this.appVisibilitySubscription = this.assetsService.appVisibilityChanged.subscribe(() => {
      void this.refreshContext();
      void this.loadSearchablePages();
    });
    void this.loadSearchablePages();
    this.notificationsSubscription = this.notificationsService.notifications$.subscribe((items) => {
      this.notifications = items.slice(0, 6);
      this.cdr.detectChanges();
    });
    this.unreadNotificationsSubscription = this.notificationsService.unreadCount$.subscribe((count) => {
      this.unreadNotifications = count;
      this.cdr.detectChanges();
    });
    this.unseenNotificationsSubscription = this.notificationsService.unseenCount$.subscribe((count) => {
      this.unseenNotifications = count;
      this.cdr.detectChanges();
    });
    this.notificationsService.start();
    void this.chatState.initialize();
    this.emailUnreadSubscription = this.emailRealtime.unreadCount$.subscribe((count) => {
      this.emailUnreadCount = count;
      this.cdr.detectChanges();
    });
    this.recentEmailsSubscription = this.emailRealtime.recentMessages$.subscribe((messages) => {
      this.recentEmails = messages;
      this.cdr.detectChanges();
    });
    this.emailRealtime.start();
  }

  ngAfterViewInit() {
    feather.replace();
  }

  toggleTheme(): void {
    this.theme = this.theme === 'dark' ? 'light' : 'dark';
    this.applyTheme();
    try {
      localStorage.setItem('tabler-theme', this.theme);
    } catch (_error) {
      // The selected theme still applies when browser storage is unavailable.
    }
  }

  @HostListener('window:storage', ['$event'])
  onThemeStorage(event: StorageEvent): void {
    if (event.key !== 'tabler-theme') return;
    this.theme = event.newValue === 'dark' ? 'dark' : 'light';
    this.applyTheme();
  }

  private applyTheme(): void {
    document.documentElement.setAttribute('data-bs-theme', this.theme);
    document.documentElement.setAttribute('data-theme', this.theme);
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.appVisibilitySubscription?.unsubscribe();
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.notificationsSubscription?.unsubscribe();
    this.notificationsSubscription = undefined;
    this.unreadNotificationsSubscription?.unsubscribe();
    this.unreadNotificationsSubscription = undefined;
    this.unseenNotificationsSubscription?.unsubscribe();
    this.unseenNotificationsSubscription = undefined;
    this.personalSettingsSubscription?.unsubscribe();
    this.personalSettingsSubscription = undefined;
    this.avatarSubscription?.unsubscribe();
    this.avatarSubscription = undefined;
    this.emailUnreadSubscription?.unsubscribe();
    this.emailUnreadSubscription = undefined;
    this.recentEmailsSubscription?.unsubscribe();
    this.recentEmailsSubscription = undefined;
    this.organisationSwitchedSubscription?.unsubscribe();
    this.organisationSwitchedSubscription = undefined;
    this.badgeRequest += 1;
  }

  async getServices() {
    let services = await this.assetsService.getServices();
    this.services = services.map(group => ({
      ...group,
      items: group.items.map((item: any) => ({
        ...item,
        icon: this.sanitizer.bypassSecurityTrustHtml(item.icon) as SafeHtml
      }))
    }));
    this.cdr.detectChanges();
  }

  async loadSearchablePages(): Promise<void> {
    this.searchLoading = true;
    try {
      await this.headerService.initialize();
      void this.updateSearchResults();
    } finally {
      this.searchLoading = false;
      this.cdr.detectChanges();
    }
  }

  onSearch(value: string): void {
    this.searchQuery = value;
    this.searchOpen = true;
    void this.updateSearchResults();
  }

  openSearch(event: Event): void {
    event.stopPropagation();
    this.searchOpen = true;
    void this.updateSearchResults();
  }

  clearSearch(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.searchQuery = '';
    this.searchResults = [];
    this.searchRequest += 1;
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.globalSearchInput?.nativeElement.focus();
  }

  openSearchResult(result: HeaderSearchResult, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.searchOpen = false;
    this.searchQuery = '';
    this.searchResults = [];
    void this.router.navigateByUrl(result.link!);
  }

  trackSearchResult(_index: number, item: HeaderSearchResult): string {
    return `${item.resultType}:${item.link}`;
  }

  private async updateSearchResults(): Promise<void> {
    const query = this.searchQuery.trim();
    if (!query) {
      this.searchResults = [];
      this.searchLoading = false;
      return;
    }
    this.searchLoading = true;
    this.cdr.detectChanges();

    if (this.searchTimer) clearTimeout(this.searchTimer);
    const request = ++this.searchRequest;
    await new Promise<void>((resolve) => {
      this.searchTimer = setTimeout(resolve, 250);
    });
    if (request !== this.searchRequest || query !== this.searchQuery.trim()) return;

    const results = await this.headerService.search(query);
    if (request !== this.searchRequest || query !== this.searchQuery.trim()) return;
    this.searchResults = results;
    this.searchLoading = false;
    this.cdr.detectChanges();
  }

  async getUser() {
    try { this.user = await firstValueFrom(this.personalSettings.get()); }
    catch { this.user = await this.authService.getUser(); }
    if (!this.destroyed) this.cdr.detectChanges();
  }

  async refreshContext(): Promise<void> {
    if (this.contextLoading || this.destroyed) return;
    this.contextLoading = true; this.contextError = '';
    const results = await Promise.allSettled([
      this.getUser(), this.getServices(),
      ...(this.loadOrganisationContext ? [this.getCurrentOrganisation(), this.getOrganisations()] : []),
    ]);
    if (this.destroyed) return;
    this.contextLoading = false;
    this.contextError = results.some(result=>result.status==='rejected')
      ? 'Navigation context could not be loaded.' : '';
    this.cdr.detectChanges();
  }

  retryContext():void {
    void this.refreshContext();
    this.assetsService.appVisibilityChanged.next();
  }

  async getCurrentOrganisation() {
    this.currentOrganisation = await this.authService.getCurrentOrganisation();
    this.cdr.detectChanges();
  }

  async getOrganisations() {
    this.organisations = await this.companyService.getOrganisationsIAdmin()
    this.cdr.detectChanges();
  }

  async logout(): Promise<void> {
    this.profileDropdownOpen = false;

    // Start server-side cleanup while the access token is still available, but
    // never let either remote request prevent the local session from ending.
    void this.notificationsService.stop(true).catch(() => undefined);
    void firstValueFrom(this.authService.logout()).catch(() => undefined);

    window.location.reload();
  }

  async selectOrganisation(organisationId: string) {
    this.organisationDropdownOpen = false;
    await this.authService.switchOrganisation(organisationId, '/home');
  }

  toggleServicesDropdown(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.servicesDropdownOpen = !this.servicesDropdownOpen;
    if (this.servicesDropdownOpen) {
      this.organisationDropdownOpen = false;
      this.notificationsDropdownOpen = false;
      this.emailDropdownOpen = false;
      this.profileDropdownOpen = false;
    }
  }

  toggleOrganisationDropdown(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.organisationDropdownOpen = !this.organisationDropdownOpen;
    if (this.organisationDropdownOpen) {
      this.servicesDropdownOpen = false;
      this.notificationsDropdownOpen = false;
      this.emailDropdownOpen = false;
      this.profileDropdownOpen = false;
    }
  }

  toggleNotificationsDropdown(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.notificationsDropdownOpen = !this.notificationsDropdownOpen;
    if (this.notificationsDropdownOpen) {
      this.servicesDropdownOpen = false;
      this.organisationDropdownOpen = false;
      this.emailDropdownOpen = false;
      this.profileDropdownOpen = false;
      void this.notificationsService.markAsSeen(this.notifications).catch(() => undefined);
    }
  }

  toggleEmailDropdown(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.emailDropdownOpen = !this.emailDropdownOpen;
    if (this.emailDropdownOpen) {
      this.servicesDropdownOpen = false;
      this.organisationDropdownOpen = false;
      this.notificationsDropdownOpen = false;
      this.profileDropdownOpen = false;
      void this.emailRealtime.refreshUnreadCount();
    }
  }

  toggleProfileDropdown(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.profileDropdownOpen = !this.profileDropdownOpen;
    if (this.profileDropdownOpen) {
      void this.refreshProfileBadges();
      this.servicesDropdownOpen = false;
      this.organisationDropdownOpen = false;
      this.notificationsDropdownOpen = false;
      this.emailDropdownOpen = false;
    }
  }

  private async refreshProfileBadges(): Promise<void> {
    const request = ++this.badgeRequest;
    this.activeEmploymentCount = null;
    this.pendingVerificationCount = null;
    this.cdr.detectChanges();
    const [employmentResult, verificationResult] = await Promise.allSettled([
      this.employments.countMyActiveEmployments(),
      firstValueFrom(this.documents.verificationRequests()),
    ]);
    if (request !== this.badgeRequest) return;
    if (employmentResult.status === 'fulfilled') {
      this.activeEmploymentCount = employmentResult.value;
    }
    if (verificationResult.status === 'fulfilled') {
      this.pendingVerificationCount = verificationResult.value.filter(
        (item) => item.status === 'pending' && !item.has_decided,
      ).length;
    }
    this.cdr.detectChanges();
  }

  @HostListener('document:click')
  closeAllDropdowns(): void {
    this.servicesDropdownOpen = false;
    this.organisationDropdownOpen = false;
    this.notificationsDropdownOpen = false;
    this.emailDropdownOpen = false;
    this.profileDropdownOpen = false;
    this.searchOpen = false;
  }

  @HostListener('document:keydown', ['$event'])
  focusSearch(event: KeyboardEvent): void {
    if (event.key !== '/' || event.ctrlKey || event.metaKey || event.altKey) return;
    const target = event.target as HTMLElement | null;
    if (target?.matches('input, textarea, [contenteditable="true"]')) return;
    event.preventDefault();
    this.searchOpen = true;
    this.globalSearchInput?.nativeElement.focus();
  }

  async markAllNotificationsRead(event: Event): Promise<void> {
    event.preventDefault();
    event.stopPropagation();
    await this.notificationsService.markAllAsRead();
  }

  openNotificationsCenter(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.notificationsDropdownOpen = false;
    this.router.navigate(['/personal/notifications']);
  }

  openEmail(message: EmailMessageSummary, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.emailDropdownOpen = false;
    void this.router.navigate(['/mail', message.id], {
      queryParams: { account_id: message.account_id },
    });
  }

  openEmailCenter(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.emailDropdownOpen = false;
    void this.router.navigate(['/mail']);
  }

  emailSender(message: EmailMessageSummary): string {
    return message.from.name?.trim() || message.from.address;
  }

  emailInitials(message: EmailMessageSummary): string {
    return this.emailSender(message)
      .split(/[\s._-]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join('') || '?';
  }

  emailPreview(message: EmailMessageSummary): string {
    return message.preview
      .replace(/<style[\s\S]*?<\/style>/gi, ' ')
      .replace(/<[^>]+>/g, ' ')
      .replace(/\b(?:a|body|div|html|img|li|ol|p|span|sup|table|td|th|ul)\s*\{[^}]*\}/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim() || 'Open this email to read the message.';
  }

  async openNotification(notification: AppNotification, event: Event): Promise<void> {
    event.preventDefault();
    event.stopPropagation();
    this.notificationsDropdownOpen = false;
    await this.notificationsService.open(notification);
  }

  trackNotification(_index: number, item: AppNotification): string {
    return item.recipientId;
  }

  getNotificationBadgeClass(notification: AppNotification): string {
    if (notification.notificationId.includes('changes-requested')) return 'bg-danger-lt text-danger';
    if (notification.notificationId.includes('accepted')) return 'bg-success-lt text-success';
    return 'bg-primary-lt text-primary';
  }

  notificationTitle(notification: AppNotification): string {
    return this.notificationsService.titleFor(notification);
  }

  notificationMessage(notification: AppNotification): string {
    return this.notificationsService.messageFor(notification);
  }

  notificationLabel(notification: AppNotification): string {
    return this.notificationsService.labelFor(notification);
  }

  get userDisplayName(): string {
    const fullNameCandidate = [
      this.user?.full_name,
      this.user?.fullName,
      this.user?.name,
    ].find((candidate) => typeof candidate === 'string' && candidate.trim().length > 0);

    if (fullNameCandidate) {
      return fullNameCandidate.trim();
    }

    const firstName = `${this.user?.first_name ?? this.user?.firstName ?? ''}`.trim();
    const lastName = `${this.user?.last_name ?? this.user?.lastName ?? ''}`.trim();
    const combinedName = `${firstName} ${lastName}`.trim();

    if (combinedName) {
      return combinedName;
    }

    const email = `${this.user?.email ?? ''}`.trim();
    if (email) {
      return email.split('@')[0];
    }

    return 'User';
  }

  get userInitials(): string {
    const firstName = `${this.user?.first_name ?? this.user?.firstName ?? ''}`.trim();
    const lastName = `${this.user?.last_name ?? this.user?.lastName ?? ''}`.trim();

    const firstInitial = firstName.charAt(0);
    const lastInitial = lastName.charAt(0);

    if (firstInitial || lastInitial) {
      return `${firstInitial}${lastInitial}`.toUpperCase();
    }

    const displayName = this.userDisplayName;
    const parts = displayName
      .split(/\s+/)
      .map((part) => part.trim())
      .filter((part) => part.length > 0);

    if (parts.length === 0) {
      return 'U';
    }

    if (parts.length === 1) {
      return parts[0].slice(0, 2).toUpperCase();
    }

    return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
  }
}
